<template>
  <footer :class="['footer-links', { 'footer-links--fixed': fixed }]">
    <div class="footer-content">
      <span class="footer-copy">{{ $t("footer.copyright") }}</span>
      <template v-for="link in links" :key="link.to">
        <span class="footer-separator">•</span>
        <router-link v-slot="{ href, navigate }" :to="link.to" custom>
          <a :href="href" @click="goToPage($event, navigate)">
            {{ $t(link.label) }}
          </a>
        </router-link>
      </template>
      <span class="footer-separator">•</span>
      <button type="button" class="footer-button" @click="openCookies">
        {{ $t("cookies.footerLink") }}
      </button>
    </div>
    <div class="footer-social">
      <a
        href="https://www.tiktok.com/@wellnotesapp"
        target="_blank"
        rel="noopener"
        aria-label="Wellnotes on TikTok"
        class="footer-social-icon"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
        </svg>
      </a>
      <a
        href="https://www.instagram.com/wellnotes.app"
        target="_blank"
        rel="noopener"
        aria-label="Wellnotes on Instagram"
        class="footer-social-icon"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      </a>
    </div>
    <span class="footer-trademark">{{ $t("footer.trademark") }}</span>
  </footer>
</template>

<script>
import { nextTick } from "vue";
import { openCookieSettings } from "../stores/consentStore";

export default {
  name: "AppFooter",
  props: {
    fixed: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    links() {
      return [
        { to: "/privacy", label: "navigation.privacy" },
        { to: "/terms", label: "navigation.terms" },
        { to: "/help", label: "navigation.help" },
        { to: "/legal", label: "navigation.legal" },
        { to: "/compliance", label: "navigation.compliance" },
      ];
    },
  },
  methods: {
    openCookies() {
      openCookieSettings();
    },
    async goToPage(event, navigate) {
      event.currentTarget.blur();
      await navigate(event);
      await nextTick();

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    },
  },
};
</script>

<style scoped>
.footer-links {
  font-family: var(--app-font);
  font-size: 13px;
  text-align: center;
  padding-top: 28px;
  padding-bottom: 30px;
  width: 100%;
  background-color: transparent;
  color: var(--app-text-muted);
  margin-top: 32px;
  border-top: 1px solid var(--app-border);
}

.footer-links--fixed {
  position: fixed;
  bottom: 0;
  left: 0;
  margin-top: 0;
}

@media (prefers-color-scheme: light) {
  .footer-links,
  .footer-links a {
    color: var(--app-text-muted);
  }
}

.footer-content {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 10px;
  row-gap: 6px;
  max-width: 100%;
  padding: 0 20px;
  line-height: 1.6;
}

.footer-content a {
  color: inherit;
  text-decoration: none;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.footer-content a:hover {
  opacity: 1;
  text-decoration: underline;
}

.footer-button {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
  transition: opacity 0.2s;
}

.footer-button:hover {
  opacity: 1;
  text-decoration: underline;
}

.footer-copy {
  opacity: 0.7;
}

.footer-separator {
  opacity: 0.4;
}

.footer-trademark {
  opacity: 0.7;
  transform: translateY(10px);
  display: inline-block;
}

.footer-social {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  margin-top: 12px;
}
.footer-social-icon {
  display: inline-flex;
  /* Couleur d'auteur explicite : sans elle, les <a> retombent sur les
     couleurs navigateur — bleu, puis VIOLET une fois le profil visité. */
  color: var(--app-text-muted);
  opacity: 0.75;
  transition: opacity 0.15s ease, color 0.15s ease;
}
.footer-social-icon:hover {
  opacity: 1;
}
</style>
